"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  analysePrompt,
  buildScholarshipPlan,
  parseCriteria,
  parseStories,
  type ScholarshipInput,
} from "@/lib/tool-kit/admissions/scholarship-essay";

/** English strings. A translated content file overrides these through `ui`. */
export const SCHOLARSHIP_ESSAY_PLANNER_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  promptHeading: "1. The prompt",
  nameLabel: "Scholarship name (optional)",
  namePlaceholder: "Riverside Community Scholarship",
  promptLabel: "Paste the essay prompt exactly as the scholarship gives it",
  promptPlaceholder:
    "Describe a time you led others through a difficulty. Explain what you learned and how it will shape your studies. (500 words)",
  wordsLabel: "Word limit",
  wordsHint: "Leave a few words of margin below the limit.",
  detectedWords: "The prompt mentions {limit}.",
  detectedRange: "{min}–{max} words",
  detectedMax: "up to {max} words",
  useDetected: "Use {max} as the limit",
  detectedCharacters:
    "The prompt gives a limit of {count} characters, not words; this planner plans in words, so convert with care or use the Character Counter.",
  criteriaHeading: "2. Criteria",
  criteriaLabel: "What the scholarship says it rewards — one per line",
  criteriaHint:
    "Copy them from the scholarship's page: leadership, financial need, service, a field of study.",
  criteriaPlaceholder: "Leadership\nCommunity service\nAcademic goals",
  storiesHeading: "3. Story bank",
  storiesLabel: "Your real experiences — one per line, “Title — what it shows”",
  storiesHint:
    "Only things that happened. A story you can tell in detail beats a list of titles.",
  storiesPlaceholder:
    "Food bank shift captain — organised 12 volunteers\nPhysics club robotics build — led a team through a failed design",
  mapPanel: "4. Match criteria to stories",
  mapLabel: "Story for “{criterion}”",
  mapNone: "— no story yet —",
  outlinePanel: "5. Outline details (optional)",
  hookLabel: "Opening — the moment you will start with",
  hookPlaceholder: "The night the robot failed in front of the judges…",
  purposeLabel: "Closing — what the scholarship will let you do",
  purposePlaceholder: "It would let me finish the degree without night shifts…",
  fileName: "scholarship-essay-plan",
  document: {
    heading: "Your scholarship essay plan",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Scholarship Essay Plan",
  titleFor: "{name}: Essay Plan",
  subtitle: "Prompt analysis, criteria map and outline",
  subtitleWords: "Prompt analysis, criteria map and outline · {words} words",
  asksHeading: "What the Prompt Asks",
  asksNone:
    "Paste the prompt above and each thing it asks for will be listed here. Your essay has to answer every one.",
  asksHead: { sentence: "The prompt says", needs: "So the essay must" },
  needsQuestion: "Answer the question directly.",
  directiveMeanings: {
    describe: "Say what happened, concretely: who, where, what you did.",
    explain: "Give reasons and causes, not only facts.",
    discuss: "Look at more than one side or aspect, then draw a conclusion.",
    reflect: "Say what you thought and learned, not only what happened.",
    evaluate: "Judge how well something worked, with evidence.",
    compare: "Show similarities and differences.",
    analyze: "Break it into parts and show how they connect.",
    argue: "Take a position and support it.",
    identify: "Name the specific thing or things.",
    demonstrate: "Show it through an example, not a claim.",
    justify: "Give the reasons for a choice or view.",
    outline: "Set out the main points, briefly.",
    summarize: "Give the short version, then move on.",
    share: "Give a first-person account in your own words.",
    why: "Give reasons.",
    how: "Describe the means or the process.",
    what: "Name or define it.",
    who: "Name the people and their part.",
  },
  mapHeading: "Criteria and Stories",
  mapHead: {
    criterion: "Criterion",
    story: "Your story",
    shows: "What it shows",
  },
  mapNoStory: "—",
  mapPrompts: {
    criteria:
      "List the criteria the scholarship rewards, then match each to a real experience.",
    stories: "Add your stories to the story bank.",
  },
  gapNote:
    "No story yet for: {criteria}. Find a real example, or ask whether this criterion needs its own section.",
  unusedNote: "Not used yet: {stories}.",
  sharedNote:
    "One story answers more than one criterion: {stories}. That can work if it is a rich story, but repeating it can look thin.",
  opening: "Opening",
  closing: "Closing",
  bodyFor: "Body: {criterion}",
  bodyGeneric: "Body: your main point",
  target: "Target: about {words} words.",
  crowdedNote:
    "Under {min} words is tight for a point with an example. Consider covering fewer criteria in depth, or ask which two or three the scholarship stresses.",
  tips: {
    opening:
      "Start with a specific moment, and signal which question you are answering.",
    body: "One point, one example, then what it shows. Use the story and its detail.",
    closing:
      "Say what you will do with the award, and tie it back to your opening.",
  },
  prompts: {
    opening: "Open with one specific moment that leads into the prompt.",
    body: "Write the point, the example from your story, and what it shows.",
    closing:
      "Say what you will do with the scholarship and how it connects to your goals.",
  },
  yourStory: "Your story",
  checklistHeading: "Before You Submit",
  checklist: [
    "My essay answers every ask listed above, in the order the prompt gives them.",
    "I am within the word limit, with a few words to spare.",
    "Every story is true, and each shows what I did, not only what happened.",
    "I checked the scholarship's page for eligibility, deadline and required documents.",
    "I asked recommenders early and told them the deadline.",
    "I proofread it aloud, and someone else read it.",
  ],
  example: {
    name: "Riverside Community Scholarship",
    prompt:
      "The Riverside Scholarship supports students who serve their communities. Describe a time you led others through a difficulty. Explain what you learned and how it will shape your studies. (500 words maximum)",
    words: "500",
    criteria: "Leadership\nCommunity service\nAcademic goals",
    stories:
      "Food bank shift captain — organised 12 volunteers over a flood week\nRobotics build — led a team through a failed design\nPhysics lab assistant — explained fluids to first-years",
    hook: "The night our robot failed in front of the judges, I had four minutes to decide whether to blame the sensor or the design.",
    purpose:
      "The award would let me drop my night shifts and take on the research placement my department offers.",
  },
} as const;

type Strings = typeof SCHOLARSHIP_ESSAY_PLANNER_STRINGS;

const EMPTY: ScholarshipInput = {
  name: "",
  prompt: "",
  words: "",
  criteria: "",
  stories: "",
  choices: {},
  hook: "",
  purpose: "",
};

type TextKey = Exclude<keyof ScholarshipInput, "choices">;

function DetectedLimit({
  strings,
  prompt,
  onUse,
}: {
  strings: Strings;
  prompt: string;
  onUse: (max: number) => void;
}) {
  const fmt = useToolNumber();
  const { words, characters } = useMemo(() => analysePrompt(prompt), [prompt]);
  if (words === null && characters === null) {
    return null;
  }
  const label = words
    ? t(words.min === null ? strings.detectedMax : strings.detectedRange, {
        min: fmt(words.min ?? 0),
        max: fmt(words.max),
      })
    : "";
  return (
    <div aria-live="polite" className="space-y-2">
      {words ? (
        <p className={`${HINT} flex flex-wrap items-center gap-2`}>
          {t(strings.detectedWords, { limit: label })}
          <button
            className={BUTTON_SMALL}
            onClick={() => onUse(words.max)}
            type="button"
          >
            {t(strings.useDetected, { max: fmt(words.max) })}
          </button>
        </p>
      ) : null}
      {characters === null ? null : (
        <p className={HINT}>
          {t(strings.detectedCharacters, { count: fmt(characters) })}
        </p>
      )}
    </div>
  );
}

export default function ScholarshipEssayPlannerWidget() {
  const strings = useToolUi(SCHOLARSHIP_ESSAY_PLANNER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<ScholarshipInput>(EMPTY);
  const doc = useMemo(
    () => buildScholarshipPlan(input, strings, fmt),
    [input, strings, fmt]
  );
  const criteria = useMemo(
    () => parseCriteria(input.criteria),
    [input.criteria]
  );
  const stories = useMemo(() => parseStories(input.stories), [input.stories]);
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const storyOptions = [
    { value: "", label: strings.mapNone },
    ...stories.map((story) => ({ value: story.title, label: story.title })),
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput({ ...EMPTY, ...strings.example })}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.promptHeading}>
        <TextField
          id="sep-name"
          label={strings.nameLabel}
          onChange={update("name")}
          placeholder={strings.namePlaceholder}
          value={input.name}
        />
        <TextAreaField
          id="sep-prompt"
          label={strings.promptLabel}
          onChange={update("prompt")}
          placeholder={strings.promptPlaceholder}
          prose
          rows={5}
          value={input.prompt}
        />
        <div className="max-w-xs">
          <TextField
            hint={strings.wordsHint}
            id="sep-words"
            label={strings.wordsLabel}
            numeric
            onChange={update("words")}
            value={input.words}
          />
        </div>
        <DetectedLimit
          onUse={(max) => update("words")(String(max))}
          prompt={input.prompt}
          strings={strings}
        />
      </Panel>

      <Panel title={strings.criteriaHeading}>
        <TextAreaField
          hint={strings.criteriaHint}
          id="sep-criteria"
          label={strings.criteriaLabel}
          onChange={update("criteria")}
          placeholder={strings.criteriaPlaceholder}
          prose
          rows={4}
          value={input.criteria}
        />
      </Panel>

      <Panel title={strings.storiesHeading}>
        <TextAreaField
          hint={strings.storiesHint}
          id="sep-stories"
          label={strings.storiesLabel}
          onChange={update("stories")}
          placeholder={strings.storiesPlaceholder}
          prose
          rows={4}
          value={input.stories}
        />
      </Panel>

      {criteria.length > 0 && stories.length > 0 ? (
        <Panel title={strings.mapPanel}>
          {criteria.map((criterion, index) => (
            <SelectField
              id={`sep-map-${index}`}
              key={criterion}
              label={t(strings.mapLabel, { criterion })}
              onChange={(title) =>
                setInput((previous) => ({
                  ...previous,
                  choices: { ...previous.choices, [criterion]: title },
                }))
              }
              options={storyOptions}
              value={input.choices[criterion] ?? ""}
            />
          ))}
        </Panel>
      ) : null}

      <Panel title={strings.outlinePanel}>
        <TextAreaField
          id="sep-hook"
          label={strings.hookLabel}
          onChange={update("hook")}
          placeholder={strings.hookPlaceholder}
          prose
          rows={2}
          value={input.hook}
        />
        <TextAreaField
          id="sep-purpose"
          label={strings.purposeLabel}
          onChange={update("purpose")}
          placeholder={strings.purposePlaceholder}
          prose
          rows={2}
          value={input.purpose}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="sep"
        labels={strings.document}
      />
    </div>
  );
}
